let pdfJsPromise = null;
let workerConfigured = false;

function getPublicBase() {
  return String(
    process.env.PUBLIC_URL || ''
  ).replace(/\/$/, '');
}

export async function loadPdfJs() {
  if (!pdfJsPromise) {
    const moduleUrl =
      `${getPublicBase()}/pdf.mjs`;

    pdfJsPromise = import(
      /* webpackIgnore: true */
      moduleUrl
    );
  }

  const pdfJs =
    await pdfJsPromise;

  if (!workerConfigured) {
    pdfJs.GlobalWorkerOptions.workerSrc =
      `${getPublicBase()}/pdf.worker.min.mjs`;

    workerConfigured = true;
  }

  return pdfJs;
}

export async function configurePdfJsWorker() {
  return loadPdfJs();
}

export function isPdfFile(file) {
  if (!file) return false;

  return (
    String(file.type || '').toLowerCase() === 'application/pdf' ||
    String(file.name || '').toLowerCase().endsWith('.pdf')
  );
}

export function isDocxFile(file) {
  if (!file) return false;

  return (
    String(file.type || '').toLowerCase() ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    String(file.name || '').toLowerCase().endsWith('.docx')
  );
}

export function isInlineReviewableFile(file) {
  return isPdfFile(file) || isDocxFile(file);
}

export async function validateSelectablePdf(file) {
  if (!isPdfFile(file)) return true;

  let data;

  try {
    data =
      new Uint8Array(
        await file.arrayBuffer()
      );
  } catch (error) {
    console.error(
      'Could not read PDF bytes:',
      error
    );

    throw new Error(
      'This file could not be read. Please choose the file again.'
    );
  }

  /*
   * First establish that this really is a PDF.
   *
   * Do not rely only on the filename/MIME type, because a renamed
   * non-PDF file should still be rejected even if PDF.js later
   * happens to fail for an unrelated reason.
   *
   * The PDF header is normally at byte zero, but the specification
   * permits readers to find it near the beginning of the file.
   */
  const headerLength =
    Math.min(
      data.length,
      1024
    );

  let header = '';

  for (
    let i = 0;
    i < headerLength;
    i += 1
  ) {
    header +=
      String.fromCharCode(
        data[i]
      );
  }

  if (
    !header.includes('%PDF-')
  ) {
    throw new Error(
      'This file is not a valid PDF.'
    );
  }

  /*
   * Selectable-text checking is useful, but it must never turn a
   * PDF.js/browser compatibility problem into a student submission
   * failure.
   *
   * If PDF.js successfully opens the document, retain the existing
   * rule: at least one page must contain selectable text.
   *
   * If PDF.js itself cannot inspect an otherwise genuine PDF,
   * permit the upload. The original file is still uploaded and can
   * be reviewed normally rather than preventing the student from
   * submitting at all.
   */
  let pdfJs;

  try {
    pdfJs =
      await configurePdfJsWorker();
  } catch (error) {
    console.warn(
      'PDF validation library could not be loaded; allowing genuine PDF:',
      error
    );

    return true;
  }

  const loadingTask =
    pdfJs.getDocument({
      data
    });

  let pdf = null;
  let inspectionFailed = false;

  try {
    pdf =
      await loadingTask.promise;

    for (
      let pageNumber = 1;
      pageNumber <= pdf.numPages;
      pageNumber += 1
    ) {
      const page =
        await pdf.getPage(
          pageNumber
        );

      const textContent =
        await page.getTextContent();

      const hasText =
        (
          textContent?.items ||
          []
        ).some(
          item =>
            String(
              item?.str ||
              ''
            )
              .replace(
                /\s+/g,
                ''
              )
              .length > 0
        );

      if (hasText) {
        return true;
      }
    }
  } catch (error) {
    inspectionFailed = true;

    console.warn(
      'PDF.js could not inspect a genuine PDF; allowing submission:',
      error
    );
  } finally {
    try {
      if (pdf) {
        await pdf.destroy();
      } else {
        await loadingTask.destroy();
      }
    } catch {
      // Cleanup failure must not block submission.
    }
  }

  if (inspectionFailed) {
    return true;
  }

  throw new Error(
    'This PDF does not contain selectable text. Please upload a PDF with selectable text.'
  );
}